import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import OrderStatusModal from "./OrderStatusModal";
import OrderDetailsModal from "./OrderDetailsModal";
import Pagination from "../Common/Pagination";
import { 
  MagnifyingGlassIcon, 
  ChevronDownIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";

const OrdersTable = ({
  orders,
  onUpdateOrder,
  currentPage = 1,
  totalPages = 1,
  total = 0,
  itemsPerPage = 10,
  onPageChange,
  onFilter
}) => {
  const { t, tStatus } = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const startIndex = (currentPage - 1) * itemsPerPage;

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (onFilter) {
      onFilter({ searchTerm: value });
    }
  };
  
  const handleSort = (sortValue) => {
    setSortBy(sortValue);
    setShowSortDropdown(false);
    if (onFilter) {
      onFilter({ sortBy: sortValue });
    }
  };
  
  const handleDateRange = () => {
    if (onFilter) {
      onFilter({ startDate, endDate });
    }
    setShowDateDropdown(false);
  };
  
  const handleClearFilters = () => {
    setSearchTerm("");
    setSortBy("");
    setStartDate("");
    setEndDate("");
    if (onFilter) {
      onFilter({ searchTerm: "", sortBy: "", startDate: "", endDate: "" });
    }
  };

  return (
    <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
      {/* Dark Header */}
      <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-semibold text-lg">{t("orders.title")}</h2>
          <span className="text-white font-semibold">({total})</span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
            <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
            <input
              type="text"
              placeholder={t("common.search")}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
            />
          </div>
          
          <div className="relative">
            <button 
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light"
            >
              <span>{sortBy ? sortBy.replace("-", " ").toUpperCase() : t("common.sortBy")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>
            {showSortDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 py-1">
                <button onClick={() => handleSort("date-desc")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t("orders.sort.dateNewest")}</button>
                <button onClick={() => handleSort("date-asc")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t("orders.sort.dateOldest")}</button>
                <button onClick={() => handleSort("price-desc")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t("orders.sort.priceHighToLow")}</button>
                <button onClick={() => handleSort("price-asc")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t("orders.sort.priceLowToHigh")}</button>
              </div>
            )}
          </div>
          
          <div className="relative">
            <button 
              onClick={() => setShowDateDropdown(!showDateDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light"
            >
              <span>{t("common.dateRange")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>
            {showDateDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 p-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t("common.fromDate")}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t("common.toDate")}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
                    />
                  </div>
                  <button
                    onClick={handleDateRange}
                    className="w-full bg-themeOrange text-white px-3 py-1.5 rounded-md text-sm"
                  >
                    {t("common.apply")}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 border border-white rounded text-white text-sm font-light hover:bg-white/10"
          >
            {t("common.clear")}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashboardStroke">
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.orderNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.buyerName")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.sellerName")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.airwayBillNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.subTotal")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.total")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.orderStatus")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("orders.col.deliveryStatus")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => {
              // Calculate subtotal from invoice items
              const subTotal = order.invoiceItems?.reduce(
                (acc, item) => acc + (item.unitPrice * item.quantity || 0),
                0
              ) || 0;
              // Total from backend (includes subtotal + shipping - discount)
              const totalPrice = order.invoiceValue || 0;
              // Get buyer and seller names
              const buyerName = order.userId?.name || order.customerName || "N/A";
              const sellerName = order.sellerId?.name || "N/A";
              // Buyer profile link — drills into /users/<id>?tab=orders&orderId=<id>
              const buyerId = order.userId?._id || order.userId;
              const buyerHref = buyerId
                ? `/users/${buyerId}?tab=orders&orderId=${order._id}`
                : null;

              return (
                <tr 
                  key={order._id}
                  className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                    index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                  }`}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardCoral">
                    #{order.customOrderId || order._id?.slice(-6) || "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {buyerHref ? (
                      <Link to={buyerHref} className="text-dashboardCoral hover:underline">
                        {buyerName}
                      </Link>
                    ) : buyerName}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {sellerName}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {order.awb || "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {Number(subTotal).toFixed(2)} {order.displayCurrencyIso || "KD"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardDarkText">
                    {Number(totalPrice).toFixed(2)} {order.displayCurrencyIso || "KD"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      className="text-dashboardCoral hover:underline font-medium"
                      onClick={() => setSelectedOrder(order)}
                    >
                      {tStatus(order.orderStatus)}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      className="text-dashboardCoral hover:underline font-medium"
                      onClick={() => setSelectedOrder(order)}
                    >
                      {tStatus(order.deliveryStatus)}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    <div>{new Date(order.createdAt).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-0.5">
                      {buyerHref ? (
                        <Link
                          to={buyerHref}
                          className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex"
                          title="View on user profile"
                        >
                          <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                        </Link>
                      ) : (
                        <button
                          onClick={() => setViewOrder(order)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                        </button>
                      )}
                      <div className="w-px h-6 bg-dashboardStroke"></div>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Edit Status"
                      >
                        <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                      </button>
                      <div className="w-px h-6 bg-dashboardStroke"></div>
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors" title="Delete">
                        <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="px-4 py-4 border-t border-dashboardStroke">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            total={total}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}

      {selectedOrder && (
        <OrderStatusModal
          order={selectedOrder}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={onUpdateOrder}
        />
      )}

      {viewOrder && (
        <OrderDetailsModal
          order={viewOrder}
          isOpen={!!viewOrder}
          onClose={() => setViewOrder(null)}
        />
      )}
    </div>
  );
};

export default OrdersTable;
